import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux"; // Import useSelector
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { updateProfile } from "../../../actions/userActions";

import { client } from "../../../services/client";
import "../donate.css"; // Your CSS file

const TIER_NAMES = { 3: "Supporter", 7: "Steward", 15: "Patron" };

// Updated price lookup with the recommended tiers.
const priceLookup = {
  3: {
    imgUrl:
      "https://cdn.sanity.io/images/ou6d2gec/production/4f561c1121a4b7e45b61ce3878f97cab97bec395-1024x1024.png",
    priceLookup: "price_1RrabXHUQmtkG68CJnalNDTm",
  }, // £3 - The Supporter
  7: {
    imgUrl:
      "https://cdn.sanity.io/images/ou6d2gec/production/56774a87aa62cda945299faa181c0b40dfdb58cf-1024x1024.png",
    priceLookup: "price_1RracdHUQmtkG68Cw00TVwk3",
  }, // £7 - The Steward
  15: {
    imgUrl:
      "https://cdn.sanity.io/images/ou6d2gec/production/84142017a20232983b1ba789efb6d77eedd8f290-1024x1024.png",
    priceLookup: "price_1Rraq3HUQmtkG68CuBlyMXKJ",
  }, // £15 - The Patron
};

function StripePaymentSection({ amount }) {
  const stripe = useStripe();
  const elements = useElements();

  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setProcessing(true);

    if (!stripe || !elements) {
      setProcessing(false);
      return;
    }

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: "https://nowasteddays.io/donate-success",
      },
    });

    if (error) {
      setErrorMessage(error.message);
    }

    setProcessing(false);
  };

  return (
    <>
      <div className="donation-section">
        <label className="donation-label">Card Details</label>
        <div className="payment-input-container">
          <PaymentElement id="payment-element" />
        </div>
      </div>

      <p className="donation-social-proof">
        Supporters like you keep NoWastedDays ad-free and available to as many
        people as possible.
      </p>

      <button
        type="button"
        className="donate-button"
        disabled={processing || !stripe || !elements}
        onClick={handleSubmit}
      >
        {processing ? "Processing..." : `Donate £${amount}`}
      </button>

      {errorMessage && <div className="payment-error">{errorMessage}</div>}
    </>
  );
}

function PaymentSkeleton() {
  return (
    <>
      <div className="donation-section">
        <label className="donation-label">Card Details</label>
        <div
          className="payment-input-container payment-input-skeleton"
          aria-busy="true"
        >
          Loading secure payment field…
        </div>
      </div>

      <p className="donation-social-proof">
        Supporters like you keep NoWastedDays ad-free and available to as many
        people as possible.
      </p>

      <button type="button" className="donate-button" disabled>
        Preparing…
      </button>
    </>
  );
}

export function DonationForm({
  amount,
  setAmount,
  frequency,
  setFrequency,
  mode = "stripe",
  canDoMonthly = false,
}) {
  const dispatch = useDispatch();

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const [pageContent, setPageContent] = useState();

  async function getPosts() {
    const posts =
      await client.fetch(`*[_type == "photo" && title match "SUPPORTER_*"]{
    _id,
    title,
    "imageUrl": imageFile.asset->url,
    altText,
    caption,
    description
  }`);
    return posts;
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getPosts();

        if (response) {
          setPageContent(response);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // When frequency changes to 'monthly', ensure the amount is a valid tier.
  useEffect(() => {
    if (frequency === "monthly" && !priceLookup[amount.toString()]) {
      setAmount(7); // Default to the recommended tier.
    }
  }, [frequency, amount]);

  const AmountSelector = () => {
    if (frequency === "monthly") {
      return (
        <>
          <div className="tier-selector">
            {Object.keys(priceLookup).map((tierAmount, index) => (
              <>
                <button
                  type="button"
                  key={tierAmount}
                  onClick={() => setAmount(Number(tierAmount))}
                  // Add a 'recommended' class to the £7 tier for special styling
                  className={`tier-button ${
                    Number(amount) === Number(tierAmount) ? "active" : ""
                  } ${Number(tierAmount) === 7 ? "recommended" : ""}`}
                >
                  £{tierAmount}{" "}
                  {/* Decorative: the amount and the tier name either side of
                      this image already say everything it conveys, so an empty
                      alt is the correct answer rather than a restatement. */}
                  <img
                    src={priceLookup[tierAmount].imgUrl}
                    alt=""
                    width="40px"
                    height="40px"
                    loading="lazy"
                  ></img>
                  <span className="tier-name">
                    {TIER_NAMES[Number(tierAmount)]}
                  </span>
                  {Number(tierAmount) === 7 && (
                    <span className="recommended-badge">Most Popular</span>
                  )}
                </button>
              </>
            ))}
          </div>
        </>
      );
    }

    return (
      <div className="input-container-donate">
        <span className="currency-symbol">£</span>
        <input
          id="donation-amount"
          className="formInput"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          min="1"
        />
      </div>
    );
  };

  return (
    <div className="donation-tile">
      <div style={{ padding: "24px" }}>
        <h2 className="donation-header">
          NoWastedDays will always be available for free but...
        </h2>
        <p className="donation-subhead" style={{ color: "#e2dfcf" }}>
          ...how many people the platform reaches and helps will be completely
          dependent on donations. If like what the platform does or is trying to
          do, and you have some money you would happily donate, you can make a
          hugely telling impact on how many people are helped by what I have
          created.
        </p>
      </div>

      <div className="donation-section">
        <label className="donation-label">Frequency</label>
        <div className="frequency-selector">
          <button
            type="button"
            onClick={() => setFrequency("one-time")}
            className={frequency === "one-time" ? "active" : ""}
          >
            One-Time
          </button>
          <button
            type="button"
            onClick={() => setFrequency("monthly")}
            className={frequency === "monthly" ? "active" : ""}
          >
            Monthly
          </button>
        </div>
      </div>

      <div className="donation-section">
        <label className="donation-label" htmlFor="donation-amount">
          Amount
        </label>
        <AmountSelector />
      </div>

      {mode === "login-required" ? (
        <div className="donation-section">
          <div className="login-cta">
            <h3 className="login-cta__heading">
              {!userInfo
                ? "Log in to set up a monthly donation"
                : "Your account isn't set up for monthly giving yet"}
            </h3>
            <p className="login-cta__body">
              Monthly giving is tied to your account so you can manage or cancel
              it any time.
            </p>
            {!userInfo && (
              <>
                <a href="/login" className="donate-button login-cta__primary">
                  Log in
                </a>
                <a href="/register" className="login-cta__secondary">
                  Create an account
                </a>
              </>
            )}
            <button
              type="button"
              className="login-cta__fallback"
              onClick={() => setFrequency("one-time")}
            >
              Or make a one-time donation instead
            </button>
          </div>
        </div>
      ) : mode === "stripe" ? (
        <StripePaymentSection amount={amount} />
      ) : (
        <PaymentSkeleton />
      )}
    </div>
  );
}
